// Everything here is resolved on use rather than captured in the constructor.
// The constructor runs once, at upgrade - before this element's children exist
// if it is defined ahead of parsing - and never again when the markup inside is
// replaced by an A/B swap or a section re-render. Listeners are delegated on the
// element itself, so they survive that replacement without needing to re-bind.
class DetailsDisclosure extends HTMLElement {
  get mainDetailsToggle() {
    return this.querySelector('details');
  }

  get content() {
    const summary = this.querySelector('details > summary');
    return summary ? summary.nextElementSibling : null;
  }

  connectedCallback() {
    // connectedCallback also fires on a plain move, and the listeners below live
    // on this element and survive that - so bind them only once.
    if (this._bound) return;
    this._bound = true;

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    // `toggle` does not bubble, so it has to be caught in the capture phase.
    this.addEventListener('toggle', this.onToggle.bind(this), true);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    const toggle = this.mainDetailsToggle;
    const content = this.content;
    if (!toggle || !content) return;

    // Re-read whenever the content element changes: animations belong to the
    // node they were read from, and a replacement panel has its own.
    if (!this.animations || this._animationsFor !== content) {
      this._animationsFor = content;
      this.animations = content.getAnimations();
    }

    if (toggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    const toggle = this.mainDetailsToggle;
    if (!toggle) return;
    toggle.removeAttribute('open');
    const summary = toggle.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  // The header wrapper is outside this element and can be replaced independently
  // of it, so it is looked up rather than held.
  get header() {
    return document.querySelector('[data-header-wrapper]');
  }

  onToggle() {
    const header = this.header;
    const toggle = this.mainDetailsToggle;
    if (!header || !toggle) return;
    header.preventHide = toggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
